import toast from "react-hot-toast";
import { Link, Outlet } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateAvatar } from "~/data/user/user.service";

import { Button } from "../shared/button";
import { UsersIcon } from "../shared/icons/users";
import { UserAvatar } from "../shared/user-avatar";
import { useUser } from "../shared/queries/user-query";

export function Profile() {
  const user = useUser();
  const queryClient = useQueryClient();
  const updateAvatarMutation = useMutation({
    mutationFn: (file: File) => updateAvatar(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user"] }),
  });
  const avatarFile = updateAvatarMutation.variables;
  const avatarUrl = avatarFile
    ? URL.createObjectURL(avatarFile)
    : user.avatar_url
    ? import.meta.env.VITE_API_URL + user.avatar_url
    : undefined;

  return (
    <div className="p-10 bg-white">
      <section className="mb-7 flex justify-between items-center">
        <h3 className="text-3xl font-semibold text-[#5B5B5B]">Meu Perfil</h3>
        <div className="flex gap-3 justify-end">
          <Button variant="primary-2" className="h-10 w-60" asChild>
            <Link to=".">Perfil</Link>
          </Button>
          <Button variant="secondary-2" className="h-10 w-60" asChild>
            <Link to="pagamentos">Pagamentos</Link>
          </Button>
        </div>
      </section>

      <section className="mb-5 flex justify-between items-center p-5 bg-[#FBFBFB] rounded-[10px]">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <UserAvatar url={avatarUrl} name={user.name} size={64} />
            <label className="absolute top-0 right-0 cursor-pointer">
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.05049 12.1201L12.5973 6.57331C11.6642 6.18349 10.8167 5.61424 10.1029 4.89786C9.38616 4.18393 8.81665 3.33616 8.42669 2.4027L2.87989 7.94951C2.44716 8.38224 2.23042 8.59898 2.04442 8.83747C1.82498 9.11907 1.63664 9.42358 1.48269 9.74569C1.35295 10.0187 1.2562 10.3097 1.06271 10.8902L0.0412452 13.9523C-0.00576786 14.0925 -0.0127429 14.243 0.0211037 14.387C0.0549504 14.5309 0.128277 14.6626 0.232842 14.7672C0.337407 14.8717 0.469064 14.9451 0.613015 14.9789C0.756966 15.0127 0.907505 15.0058 1.04771 14.9588L4.10985 13.9373C4.69108 13.7438 4.98132 13.6471 5.25431 13.5173C5.5778 13.3633 5.88054 13.1761 6.16253 12.9556C6.40102 12.7696 6.61776 12.5528 7.05049 12.1201ZM14.1362 5.03436C14.6893 4.4813 15 3.7312 15 2.94906C15 2.16692 14.6893 1.41682 14.1362 0.86376C13.5832 0.310704 12.8331 5.82739e-09 12.0509 0C11.2688 -5.82739e-09 10.5187 0.310704 9.96564 0.86376L9.30041 1.52899L9.32891 1.61223C9.65664 2.55028 10.1931 3.40166 10.8979 4.10214C11.6193 4.82798 12.5005 5.37503 13.471 5.69959L14.1362 5.03436Z"
                  fill="#5B5B5B"
                />
              </svg>

              <span className="sr-only">Editar Avatar</span>
              <input
                className="hidden"
                type="file"
                onChange={(e) => {
                  const fileList = e.target.files;
                  const file = fileList?.item(0);
                  if (!file) return;
                  updateAvatarMutation.mutate(file);
                }}
              />
            </label>
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-800">{user.name}</h4>
            {user.is_trial && user.trial_end_at && (
              <p className="text-gray-500 text-sm">
                Assinatura para Teste (Válido até{" "}
                {new Date(user.trial_end_at).toLocaleDateString("pt-BR")})
              </p>
            )}
          </div>
        </div>
        {user.user_subscription && (
          <p className="font-alternative text-sm text-[#5B5B5B] leading-4 uppercase">
            Status do seu plano:
            <span className="ml-2 text-xs align-middle font-bold text-white bg-alternative-2 px-2 py-px rounded">
              {renderSubscriptionStatus(user.user_subscription.status)}
            </span>
          </p>
        )}
      </section>

      <div className="p-4 inline-flex items-center gap-2 mb-5">
        <UsersIcon size={32} />
        <span className="uppercase text-[#092327] font-alternative text-base font-semibold leading-5">
          Código de Convite:
        </span>
        <span className="uppercase text-secondary-1 font-alternative text-2xl font-semibold">
          {user.indication_code}
        </span>
        <button
          type="button"
          aria-label="Copiar Código"
          onClick={async () => {
            await navigator.clipboard.writeText(user.indication_code);
            toast("Copiado!");
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.8335 8.05648C5.8335 7.46704 6.06765 6.90174 6.48445 6.48494C6.90125 6.06814 7.46655 5.83398 8.056 5.83398H15.2777C15.5695 5.83398 15.8585 5.89147 16.1282 6.00316C16.3978 6.11485 16.6428 6.27856 16.8492 6.48494C17.0556 6.69132 17.2193 6.93632 17.331 7.20597C17.4427 7.47562 17.5002 7.76462 17.5002 8.05648V15.2782C17.5002 15.57 17.4427 15.859 17.331 16.1287C17.2193 16.3983 17.0556 16.6433 16.8492 16.8497C16.6428 17.0561 16.3978 17.2198 16.1282 17.3315C15.8585 17.4432 15.5695 17.5007 15.2777 17.5007H8.056C7.76413 17.5007 7.47513 17.4432 7.20548 17.3315C6.93584 17.2198 6.69083 17.0561 6.48445 16.8497C6.27807 16.6433 6.11437 16.3983 6.00267 16.1287C5.89098 15.859 5.8335 15.57 5.8335 15.2782V8.05648Z"
              stroke="#979797"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3.34333 13.9475C3.0875 13.8021 2.87471 13.5916 2.72658 13.3374C2.57846 13.0832 2.50028 12.7942 2.5 12.5V4.16667C2.5 3.25 3.25 2.5 4.16667 2.5H12.5C13.125 2.5 13.465 2.82083 13.75 3.33333"
              stroke="#979797"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <Outlet />
    </div>
  );
}

function renderSubscriptionStatus(status: string) {
  switch (status) {
    case "waiting_payment":
      return "Aguardando pagamento";
    case "confirmed":
      return "Ativo";
    case "paid":
      return "Pago";
    case "created":
      return "Criado";
    default:
      return status;
  }
}
